/**
 * CNC PM PWA — Operating Hours Tracker & Service Forecast (hoursTracker.ts)
 * 
 * Tracking cumulative machine running hours against milestone service thresholds:
 * 500h, 1,000h, 2,000h (hydraulic oil renewal), 8,000h (spindle grease & battery), 10,000h (milling head).
 */

import {
  OPERATING_HOURS_TIERS,
  TIER_DESCRIPTIONS,
  type OperatingHoursTier,
  type PMStatus,
} from './statusCalculator';

export interface MilestoneProgress {
  tier: number;
  description: string;
  hoursElapsed: number;
  remainingHours: number;
  progressPercent: number; // 0 to 100
  status: PMStatus;
  statusLabelTh: string;
  estimatedDueDate: string;
  daysRemaining: number;
  lubricants?: Array<{
    code: string;
    name: string;
    action: string;
  }>;
}

export const MILESTONE_LUBRICANTS: Record<
  number,
  Array<{ code: string; name: string; action: string }>
> = {
  500: [
    { code: 'ISO VG 68', name: 'Slideway Oil', action: 'Check film on X/Y/Z ways and ballscrew' },
  ],
  1000: [
    { code: 'AP2 Grease', name: 'Lithium Grease', action: 'Manual greasing of ballscrews & bearings' },
    { code: 'ISO VG 68', name: 'Way Lubricant', action: 'Inspect pump reservoir & filters' },
  ],
  2000: [
    { code: 'O - HM 46', name: 'Hydraulic Oil', action: 'Full drain and flush renewal (Tank 200L)' },
    { code: 'O - G 220', name: 'Central Lubrication Oil', action: 'Drain and refill gearbox & central system' },
    { code: 'Filter Elements', name: 'Hydraulic Return Filter', action: 'Replace filter cartridges (10μm)' },
  ],
  8000: [
    { code: 'Klüber Isoflex NBU 15', name: 'Spindle High-Speed Grease', action: 'Spindle bearings regreasing overhaul' },
    { code: 'CNC Battery 3.6V', name: 'Lithium Memory Battery', action: 'Replace Fanuc/Heidenhain NC backup batteries' },
  ],
  10000: [
    { code: 'ISO VG 220', name: 'Heavy Gear Oil', action: 'Milling head secondary transmission fluid renewal' },
  ],
};

/**
 * Calculates detailed progress towards a specific milestone tier
 */
export function calculateMilestoneProgress(
  tier: number,
  cumulativeHours: number,
  lastServiceHours: number = 0,
  avgDailyHours: number = 16,
  now: Date = new Date()
): MilestoneProgress {
  const hoursElapsed = Math.max(0, cumulativeHours - lastServiceHours);
  const remainingHours = Math.max(0, tier - hoursElapsed);
  const progressPercent = Math.min(100, Math.max(0, (hoursElapsed / tier) * 100));

  let status: PMStatus = 'NORMAL';
  let statusLabelTh = 'ปกติ';

  if (hoursElapsed >= tier) {
    status = 'OVERDUE';
    statusLabelTh = 'เกินกำหนด (Overdue)';
  } else if (hoursElapsed >= 0.9 * tier || remainingHours <= tier * 0.1) {
    status = 'DUE_SOON';
    statusLabelTh = 'ใกล้ครบกำหนด (Due Soon)';
  }

  const daysRemaining = remainingHours / (avgDailyHours > 0 ? avgDailyHours : 16);
  const estimatedDueDate = new Date(now.getTime() + daysRemaining * 86400000).toISOString();

  return {
    tier,
    description: TIER_DESCRIPTIONS[tier] || `Service Milestone ${tier}h`,
    hoursElapsed,
    remainingHours,
    progressPercent,
    status,
    statusLabelTh,
    estimatedDueDate,
    daysRemaining: Math.round(daysRemaining * 10) / 10,
    lubricants: MILESTONE_LUBRICANTS[tier] || [],
  };
}

/**
 * Returns full forecast array across all 5 standard operating hour tiers
 */
export function getAllMilestonesForecast(
  cumulativeHours: number,
  lastServiceHours: number = 0,
  avgDailyHours: number = 16,
  now: Date = new Date()
): MilestoneProgress[] {
  return OPERATING_HOURS_TIERS.map((tier) =>
    calculateMilestoneProgress(tier, cumulativeHours, lastServiceHours, avgDailyHours, now)
  );
}

/**
 * Identifies the most immediate upcoming or active milestone
 */
export function getNextServiceMilestone(
  cumulativeHours: number,
  lastServiceHours: number = 0,
  avgDailyHours: number = 16,
  now: Date = new Date()
): MilestoneProgress {
  const all = getAllMilestonesForecast(cumulativeHours, lastServiceHours, avgDailyHours, now);

  // Return overdue milestone if any exists, prioritizing the highest overdue
  const overdues = all.filter((m) => m.status === 'OVERDUE');
  if (overdues.length > 0) {
    return overdues[overdues.length - 1]; // e.g. furthest overdue
  }

  // Next due soon milestone
  const dueSoons = all.filter((m) => m.status === 'DUE_SOON');
  if (dueSoons.length > 0) {
    return dueSoons[0];
  }

  // Next upcoming milestone (where hoursElapsed < tier)
  const upcoming = all.find((m) => m.hoursElapsed < m.tier);
  return upcoming || all[all.length - 1];
}

/**
 * Format operating hours with locale formatting and unit
 */
export function formatHours(hours: number | undefined | null, showUnit: boolean = true): string {
  if (hours === undefined || hours === null || isNaN(hours)) {
    return showUnit ? '0.0 ชม.' : '0.0';
  }
  const formatted = hours.toLocaleString('th-TH', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return showUnit ? `${formatted} ชม.` : formatted;
}
